import React, { useState, useEffect, forwardRef } from 'react'
import { Link } from 'react-router-dom'
import { Input, Select, Button, formatSelectOptions } from '../../../components'
import { useMessage } from '../../../hooks'
import { candidateService } from '../services'

// Identity (name/email/phone/linkedin/company) belongs to the linked network.person
// and is edited there, not here — this form edits recruitment-owned fields only
// (owner/source/notes/links). The person is shown read-only with a link to Nätverk.
const _CandidateEditForm = ({ data, submitForm, loading, setLoading }, ref) => {

  const candidate = data[0] || {}

  const [formData, setFormData] = useState({ owner_user_id: null, source: '', notes: '' })
  const [links, setLinks] = useState([])
  const [invalid, setInvalid] = useState({})
  const [users, setUsers] = useState([])

  const { showMessage } = useMessage()

  useEffect(() => {
    candidateService.getUsers()
      .then(res => setUsers(res.users || []))
      .catch(err => showMessage(JSON.stringify(err), 'error'))
  }, [])

  useEffect(() => {
    if (!candidate.candidate_uuid) return
    setFormData({
      owner_user_id: candidate.owner_user_id,
      source:        candidate.candidate_source || '',
      notes:         candidate.candidate_notes || '',
    })
    setLinks(candidate.candidate_links && candidate.candidate_links.length ? candidate.candidate_links : [])
  }, [data])

  const setLink = (index, field, value) => {
    setLinks(prev => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }
  const addLink = () => setLinks(prev => [...prev, { label: '', url: '' }])
  const removeLink = (index) => setLinks(prev => prev.filter((_, i) => i !== index))

  const handleInputChange = (event) => {
    let { name, value } = ''
    if (event.target !== undefined) { name = event.target.name; value = event.target.value }
    else { name = event.name; value = event.value }
    const invalidForm = { ...invalid }
    delete invalidForm[name]
    setInvalid(invalidForm)
    setFormData({ ...formData, [name]: value })
  }

  const onSubmit = (event) => {
    event.preventDefault()

    const invalidForm = {}
    if (!formData.owner_user_id) invalidForm.owner_user_id = { message: 'Du måste välja en ägare' }

    const nonEmptyLinks = links.filter(l => l.label.trim() || l.url.trim())
    nonEmptyLinks.forEach((l, i) => {
      if (!l.label.trim()) invalidForm[`link.${i}.label`] = { message: `Länk ${i + 1} saknar beskrivning` }
      if (!l.url.trim())   invalidForm[`link.${i}.url`]   = { message: `Länk ${i + 1} saknar URL` }
    })

    if (Object.keys(invalidForm).length > 0) {
      setInvalid(invalidForm)
      return
    }

    setLoading(true)
    submitForm({
      owner_user_id: Number(formData.owner_user_id),
      source:   formData.source.trim() || undefined,
      notes:    formData.notes.trim()  || undefined,
      links:    nonEmptyLinks.map(l => ({ label: l.label.trim(), url: l.url.trim() })),
    })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>

        <div className="form-block vertical">
          <label>Person</label>
          <div className="cd-person-readonly">
            {candidate.candidate_person_uuid
              ? <Link className="cd-person-link" to={`/network/persons/${candidate.candidate_person_uuid}`}>{candidate.candidate_name} ↗</Link>
              : <span>{candidate.candidate_name}</span>}
            {candidate.candidate_company_name && <span className="cd-person-company"> · {candidate.candidate_company_name}</span>}
          </div>
          <p style={{ color: 'var(--color-dark-grey)', fontSize: '0.82em', marginTop: 4 }}>
            Namn, e-post, telefon och LinkedIn hanteras i Nätverk.
          </p>
        </div>

        <div className="form-block vertical">
          <label>Ägare</label>
          <Select
            name="owner_user_id"
            value={formData.owner_user_id}
            options={formatSelectOptions({ options: users, optionValue: 'user_id', optionLabel: 'name', tooltip: 'email' })}
            onChange={handleInputChange}
            disabled={loading}
            invalid={invalid.owner_user_id}
            placeholder="Välj ägare…"
          />
        </div>

        <div className="form-block vertical">
          <Input type="text" name="source" label="Källa" value={formData.source} onChange={handleInputChange} disabled={loading} />
        </div>

        <div className="form-block vertical">
          <label>Anteckningar</label>
          <textarea
            className="cd-plain-textarea"
            name="notes"
            rows={3}
            value={formData.notes}
            onChange={handleInputChange}
            disabled={loading}
          />
        </div>

        <div className="form-block vertical">
          <label>Länkar</label>
          {links.map((l, i) => (
            <div className="cd-link-row" key={i}>
              <Input type="text" name={`link-label-${i}`} placeholder="Beskrivning, t.ex. CV" value={l.label} onChange={e => setLink(i, 'label', e.target.value)} disabled={loading} invalid={invalid[`link.${i}.label`]} />
              <Input type="text" name={`link-url-${i}`} placeholder="https://…" value={l.url} onChange={e => setLink(i, 'url', e.target.value)} disabled={loading} invalid={invalid[`link.${i}.url`]} />
              <button type="button" className="cd-plain-remove" onClick={() => removeLink(i)} disabled={loading}>Ta bort</button>
            </div>
          ))}
          <Button value="+ Lägg till länk" className="button" onClick={addLink} disabled={loading} />
        </div>

        {Object.keys(invalid).length > 0 &&
          <div className="form-block form-error">
            {Object.entries(invalid).map(([key, value]) => (<p key={key}>{value.message}</p>))}
          </div>
        }
      </form>
    </div>
  )
}

export const CandidateEditForm = forwardRef(_CandidateEditForm)
